import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import {
  Box,
  Button,
  ButtonMode,
  ButtonSize,
  IPagePanelFooter,
  PagePanel,
  PlusIcon
} from 'edenred-ui';
import { useDispatch } from 'react-redux';

import {
  useCreateBenefitGroupMutation,
  useGetBenefitGroupsQuery
} from '@epi-api/benefitGroups';
import { IBenefitGroup } from '@epi-models/pages/BenefitGroups';
import { goToPage } from '@epi-actions/navigation';
import { routerPaths } from '@epi-constants/routerPaths';
import { CREATE_BENEFIT_GROUPS_CACHE_KEY } from '@epi-constants/benefitGroups';

import { GridContainer } from './BenefitGroups.styles';
import { BenefitGroupTile } from './components/BenefitGroupTile';
import { useOnboardingWizard } from '@epi-shared/hooks';

export const BenefitGroupsList = () => {
  const { t } = useTranslation();
  const { primaryFontAltColor, fontMediumBold } = useTheme();
  const benefitGroups = useGetBenefitGroupsQuery();
  const dispatch = useDispatch();
  const [_trigger, { data: newGroupId, reset }] = useCreateBenefitGroupMutation(
    {
      fixedCacheKey: CREATE_BENEFIT_GROUPS_CACHE_KEY
    }
  );

  const { isOnBoardingActive } = useOnboardingWizard();

  const handleAddNewBenefitGroup = () => {
    dispatch(goToPage(routerPaths.createBenefitGroup));
  };

  const footer: IPagePanelFooter | undefined = isOnBoardingActive
    ? {
        onSubmit: () => {
          dispatch(goToPage(routerPaths.beneficiaries));
        },
        submitText: t('components.onboarding.buttons.next'),
        submitButtonWidth: '240px',
        disableSubmit: !benefitGroups.data?.length
      }
    : undefined;

  useEffect(() => {
    if (newGroupId) {
      return () => reset();
    }
  }, []);

  return (
    <Box>
      <PagePanel
        title={t('containers.benefit_groups.title')}
        headerContent={
          <Button
            mode={ButtonMode.Primary}
            onClick={handleAddNewBenefitGroup}
            size={ButtonSize.Medium}
            id="add_benefit_group-btn"
          >
            <Box display={'flex'} gap={1} alignItems="center">
              <PlusIcon color={primaryFontAltColor} />
              <Box fontWeight={fontMediumBold}>
                {t('containers.benefit_groups.add_benefit_group')}
              </Box>
            </Box>
          </Button>
        }
        footer={footer}
      >
        <GridContainer>
          {benefitGroups.data?.map((data: IBenefitGroup) => (
            <BenefitGroupTile
              key={data.groupId}
              benefitGroup={data}
              newGroupId={newGroupId}
            />
          ))}
        </GridContainer>
      </PagePanel>
    </Box>
  );
};
